import { useEffect, useRef } from 'react'
import invariant from 'tiny-invariant'
import { isSafari } from '../browser/index.js'
import { logger } from '@tunasong/models'

/**
 * WakeLock using the Chrome experimental Wakelock API.
 *
 * Cleanup MUST release the underlying `WakeLockSentinel` — otherwise the lock
 * survives unmount and (on macOS) keeps the laptop from sleeping when the lid
 * is closed (GH #1380). The browser only auto-releases on visibility change,
 * which doesn't fire reliably enough to be trusted as the sole release path.
 */
export const useWakeLockNavigator = (enabled = true) => {
  const activeWakeLock = useRef<WakeLockSentinel | null>(null)

  useEffect(() => {
    if (!enabled) {
      return
    }

    // Track whether this effect instance has been cleaned up. The async
    // `requestWakeLock()` may resolve AFTER cleanup runs — without this flag
    // we'd assign the sentinel to the ref after cleanup and leak it.
    let cancelled = false

    const releaseActive = async () => {
      const current = activeWakeLock.current
      if (!current) {
        return
      }
      activeWakeLock.current = null
      try {
        await current.release()
      } catch (err) {
        logger.warn('Wakelock release failed: ', err)
      }
    }

    const requestWakeLock = async () => {
      try {
        invariant('wakeLock' in navigator, 'WakeLock API not available')
        if (cancelled) {
          return
        }
        await releaseActive()
        const wakeLock = await navigator.wakeLock.request('screen')

        if (cancelled) {
          // Effect was cleaned up while the request was in-flight — release
          // the freshly-acquired lock immediately to avoid a leak.
          try {
            await wakeLock.release()
          } catch (err) {
            logger.warn('Wakelock release (post-cancel) failed: ', err)
          }
          return
        }

        // listen for our release event
        wakeLock.addEventListener('release', () => {
          activeWakeLock.current = null
        })
        activeWakeLock.current = wakeLock
      } catch (err) {
        logger.warn(`Wakelock request failed: `, err)
      }
    }

    const handleVisibilityChange = () => {
      /** Safari does not allow visibility change */
      if (!isSafari() && activeWakeLock.current === null && document.visibilityState === 'visible') {
        void requestWakeLock()
      }
    }

    const handleClick = () => {
      if (activeWakeLock.current) {
        return
      }
      void requestWakeLock()
    }

    /** Initial lock */
    handleVisibilityChange()

    document.addEventListener('visibilitychange', handleVisibilityChange)
    document.addEventListener('click', handleClick)
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      document.removeEventListener('click', handleClick)
      void releaseActive()
    }
  }, [enabled])
}
